import { MAX_ALLOWED_ARCHIVE_AFTER_X_DAYS, MIN_ALLOWED_ARCHIVE_AFTER_X_DAYS } from "@finway-group/shared/lib/consts/common"
import { isArchiveAfterXDaysValid } from "@finway-group/shared/lib/utils/validators/companySettings.validator"
import { Rule } from "antd/lib/form"
import i18n from "Shared/locales/i18n"
import { isNumeric, isValidBIC, isValidIBAN } from "Shared/utils/helper.utils"
import { stringRules } from "Components/forms/rules/common/string.rules"
import { generalRules } from "Components/forms/rules/common/general.rules"

export const createCompanySettingsRules = () => ({
    name: [
        // keep line breaks
        generalRules.required,
        stringRules.noExcessWhitespace,
        stringRules.maxLength(100),
        stringRules.allowedCompanyNameCharsOnly,
    ] as Array<Rule>,
    iban: [
        {
            validator: (_rule, value: string) => {
                // Optional
                if (!value || value === "") {
                    return Promise.resolve()
                }

                if (!isValidIBAN(value)) {
                    return Promise.reject(new Error(i18n.t("validation:iban")))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    bic: [
        {
            validator: (_rule, value: string) => {
                // Optional
                if (!value || value === "") {
                    return Promise.resolve()
                }

                if (!isValidBIC(value)) {
                    return Promise.reject(new Error(i18n.t("validation:bic")))
                }
                if (value.length > 11) {
                    return Promise.reject(new Error(i18n.t("validation:string_too_long")))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    companyIdentifier: [stringRules.allowedMultiLineCharsOnly] as Array<Rule>,
    businessAddressAddressLine: [stringRules.allowedMultiLineCharsOnly] as Array<Rule>,
    defaultCreditorNumber: [
        {
            validator: (_rule, value: string) => {
                // Optional
                if (!value || value === "") {
                    return Promise.resolve()
                }

                if (!isNumeric(value)) {
                    return Promise.reject(i18n.t("validation:numeric"))
                }
                if (value.length > 0 && value.substring(0, 1) === "0") {
                    return Promise.reject(i18n.t("validation:no_leading_zero"))
                }
                // DATEV limit
                if (value.length < 5) {
                    return Promise.reject(i18n.t("validation:string_too_short"))
                }
                if (value.length > 9) {
                    return Promise.reject(i18n.t("validation:string_too_long"))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    countryCode: [
        {
            validator: (_rule, value: string) => {
                // Optional
                if (!value || value === "") {
                    return Promise.resolve()
                }

                if (value.length !== 2) {
                    return Promise.reject(new Error(i18n.t("validation:exact.string", { num: 2 })))
                }
                if (!/[A-Z]{2}/.test(value)) {
                    return Promise.reject(new Error(i18n.t("validation:only_uppercase")))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    city: [
        {
            validator: (_rule, value: string) => {
                // Optional
                if (!value || value === "") {
                    return Promise.resolve()
                }

                if (value.length > 20) {
                    return Promise.reject(new Error(i18n.t("validation:max.string", { max: 20 })))
                }

                return Promise.resolve()
            },
        },
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    zip: [
        {
            validator: (_rule, value: string) => {
                // Optional
                if (!value || value === "") {
                    return Promise.resolve()
                }

                if (value.length > 10) {
                    return Promise.reject(new Error(i18n.t("validation:max.string", { max: 12 })))
                }
                if (!/^[A-Za-z0-9 -]*$/.test(value)) {
                    return Promise.reject(new Error(i18n.t("validation:extended_alphanumeric")))
                }

                return Promise.resolve()
            },
        },
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    archiveAfterXDays: [
        {
            validator: (_rule, value: number) => {
                if (!Number.isInteger(value)) {
                    return Promise.reject(new Error(i18n.t("validation:integer")))
                }
                if (!isArchiveAfterXDaysValid(value)) {
                    return Promise.reject(new Error(i18n.t("validation:numeric_min_max", { min: MIN_ALLOWED_ARCHIVE_AFTER_X_DAYS, max: MAX_ALLOWED_ARCHIVE_AFTER_X_DAYS })))
                }
                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    mileageDistanceBuffer: [
        { transform: (v) => Number(v), type: "number", message: i18n.t("validation:numeric") },
        { transform: (v) => Number(v), type: "number", min: 0, message: i18n.t("validation:min.number", { min: 0 }) },
    ] as Array<Rule>,
    required: [{ required: true, message: i18n.t("validation:required") }] as Array<Rule>,
})
